import template from './ko-field-input.html';

/**
 * Render fields knockout component class
 * @kind component
 * @class SampleComponent
 * @description Sample Component that shows the name value
 */
class InputFieldViewModel
{
	constructor(field)
	{
		this.field = ko_helper.safe_observable(field);
		this.readonly = ko_helper.safe_observable(field.readonly, false);
		this.multiple = ko_helper.safe_observable(field.multiple, false);
		this.help_text = ko_helper.safe_observable(field.ui_fields?.help_text, null);
		this.label = ko_helper.safe_observable(field.label, null);
		this.multiple = ko_helper.safe_observable(field.multiple, false);
		this.required = ko_helper.safe_observable(field.required, false);
		this.field_error = ko.observable(false);
		this.hidden = ko_helper.safe_observable(field.hidden || false);
		this.tooltip = ko.observable(field.tooltip || null);

		this.component_name = null;
		this.params = {};

		// Subscribe to field changes to re-render when field configuration changes
		this.field.subscribe((newField) => {
			this.render_field();
		});

		// ObservableArray for multiple values
		if (this.multiple())
		{
			let input_val = field.$input ? field.$input() : null;
			let initial = input_val || field.default_value || field.value || [];

			if (!Array.isArray(initial)) initial = initial ? [initial] : [];
			if (initial.length === 0) initial = [''];

			this.values = ko.observableArray(initial.map(v => ko.observable(v)));

			// Create a computed that syncs all values to the main $input observable
			if (field.$input && ko.isObservable(field.$input))
			{
				this.main_input = field.$input;
				this.main_input(initial);

				const update_main_input = () => {
					const all_values = this.values().map(v => v());
					this.main_input(all_values);
				};

				// Subscribe to array changes
				this.values.subscribe(() => {
					this.values().forEach(obs => {
						if (!obs._subscribed)
						{
							obs.subscribe(update_main_input);
							obs._subscribed = true;
						}
					});
					update_main_input();
				});

				// Initial subscription for existing observables
				this.values().forEach(obs => {
					if (!obs._subscribed)
					{
						obs.subscribe(update_main_input);
						obs._subscribed = true;
					}
				});

				this.main_input.subscribe((new_vals) => {
					if (Array.isArray(new_vals))
					{
						while (this.values().length < new_vals.length)
						{
							let new_obs = ko.observable('');
							new_obs.subscribe(update_main_input);
							new_obs._subscribed = true;
							this.values.push(new_obs);
						}
						// Update each individual observable
						new_vals.forEach((value, index) => {
							if (this.values()[index])
								this.values()[index](value);
						});
					}
				});
			}
			
			this.params = ko.pureComputed(() => {
				const field = this.field();
				return this.values().map(($input, idx) => {
					let params = Object.assign({}, field, field.options);
					params.$input = $input;
					params._index = idx;
					if (!field.label && field.name)
						params.label = field.name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

					return params;
				});
			});
		}
		else
		{
			this.values = null;
			this.params = null;
		}

		this.render_field();
		this.setup_validation();
	}
	
	setup_validation()
	{
		if (this.required())
		{
			if (!this.multiple() && this.params && this.params.$input)
			{
				this.params.$input.subscribe((new_value) => {
					// Clear error when user starts typing valid content
					if (this.field_error() && new_value && new_value.toString().trim() !== '')
						this.field_error(false);
					// Show error when field becomes empty and is required
					else if (this.required() && (!new_value || new_value.toString().trim() === ''))
						this.field_error(true);
				});
			}
			// multiple fields validation
			else if (this.multiple() && this.values)
			{
				this.values.subscribe(() => {
					this.values().forEach(obs => {
						if (!obs._validation_subscribed)
						{
							obs.subscribe((new_value) => {
								// Clear error when any field has valid content
								if (this.field_error() && new_value && new_value.toString().trim() !== '')
									this.field_error(false);
								// Show error when all fields are empty and required
								else if (this.required() && this.values().every(v => !v() || v().toString().trim() === ''))
									this.field_error(true);
							});
							obs._validation_subscribed = true;
						}
					});
				});
			}
		}
	}

	// Method to validate on blur (when user leaves the field)
	validate_on_blur()
	{
		if (this.required())
			this.validate();
	}
	
	validate()
	{
		if (!this.required())
			return true;

		let is_valid = true;
		
		if (this.multiple() && this.values) // Check if any of the multiple values are not empty
			is_valid = this.values().some(obs => obs() && obs().toString().trim() !== '');
		else if (this.params && this.params.$input)
		{
			// Check single field value
			let value = this.params.$input();
			is_valid = value && value.toString().trim() !== '';
		}

		this.field_error(!is_valid);
		return is_valid;
	}

	render_field()
	{
		const field = this.field();

		// Handle both string and observable types
		const fieldType = typeof field.type === 'function' ? field.type() : field.type;
		this.component_name = this.get_component_for_field_type(fieldType);

		if (!this.component_name)
		{
			console.warn(new Error(`No component for field ${field.name} with type=${fieldType}`));
			this.component_name = this.get_component_for_field_type('text');
		}

		if (!this.multiple())
		{
			let params = Object.assign({ $input: null, label: null }, field, field.options);
			params - "$raw";
			// Only set label if it's provided and not empty
			if (field.label && typeof field.label === 'string')
				params.label = field.label;
			else if (!field.label && field.name && typeof field.name === 'string')
			{
				params.label = field.name;
				params.label = params.label.replace(/_/g, ' ');
				params.label = params.label.replace(/\b\w/g, char => char.toUpperCase());
			}

			// If no label is provided, don't set it (will be null/undefined)
			if (!params.$input || !ko.isObservable(params.$input))
				params.$input = ko.observable();
			if (field.default_value)
				params.$input(field.default_value);
			this.params = params;
		}
	}

	get_component_for_field_type(name)
	{
		let field_type = Grape.registry.getItem('RenderFieldsFieldTypes', name);

		if (!field_type)
		{
			console.error(new Error(`Unknown render field type "${name}". Falling back to "text"`));
			field_type = Grape.registry.getItem('RenderFieldsFieldTypes', 'text');
		}

		if (!field_type)
			return;
		if (field_type.icomponent)
			return field_type.icomponent;
		return field_type.component;
	}

	btn_add_field_click()
	{
		if (this.multiple() && this.values)
		{
			this.values.push(ko.observable(''));

			// Update the main input observable with all current values
			if (this.main_input)
			{
				const all_values = this.values().map(v => v());
				this.main_input(all_values);
			}
		}
	}

	btn_remove_field_click(index)
	{
		if (this.multiple() && this.values && this.values().length > 1)
		{
			this.values.splice(index, 1);

			// Update the main input observable with all current values
			if (this.main_input)
			{
				const all_values = this.values().map(v => v());
				this.main_input(all_values);
			}
		}
	}
}

export default {
	name: 'ko-field-input',
	viewModel: InputFieldViewModel,
	module_type: 'ko',
	template: template
};
